import { Navigate, Route, Routes } from "react-router-dom";
import { Login } from "./pages/Login";
import { AdminRoute } from "./components/AdminRoute";
import { ScrollToTop } from "./components/ScrollToTop";
import { AdminEvents } from "./pages/AdminEvents";
import { AdminInstances } from "./pages/AdminInstances";
import { AdminToolCalls } from "./pages/AdminToolCalls";
import { AdminToolUsage } from "./pages/AdminToolUsage";
import { AuditTrail } from "./pages/AuditTrail";
import { OnboardingWizard } from "./pages/onboarding/OnboardingWizard";
import { AiModels } from "./pages/AiModels";
import { Connectors, Monitoring } from "./pages/placeholders";
import { DashboardShell } from "./pages/dashboard/DashboardShell";
import { DashboardOverview } from "./pages/dashboard/DashboardOverview";
import { DashboardSectionPreview } from "./pages/dashboard/DashboardSectionPreview";
import { IntegrationDetail } from "./pages/dashboard/IntegrationDetail";
import { IntegrationsCatalog } from "./pages/dashboard/IntegrationsCatalog";
import { AgentDetail } from "./pages/dashboard/AgentDetail";
import { ResourceDetail } from "./pages/dashboard/ResourceDetail";
import { AdminLayout } from "./pages/admin/AdminLayout";
import { AdminResources } from "./pages/AdminResources";
import { AdminFeatureFlags } from "./pages/admin/AdminFeatureFlags";
import { AdminIntegrations } from "./pages/admin/AdminIntegrations";
import { AdminSecureNotes } from "./pages/admin/AdminSecureNotes";
import { AdminInstanceDetail } from "./pages/admin/AdminInstanceDetail";
import { AdminUsers } from "./pages/admin/AdminUsers";
import { AdminInvites } from "./pages/admin/AdminInvites";
import { AdminPlugins } from "./pages/admin/AdminPlugins";
import { AdminAnnouncements } from "./pages/admin/AdminAnnouncements";
import { AdminSupportMessages } from "./pages/admin/AdminSupportMessages";
import { AdminAnnouncementDetail } from "./pages/admin/AdminAnnouncementDetail";
import { AdminRateLimits } from "./pages/admin/AdminRateLimits";
import { AdminBilling } from "./pages/admin/AdminBilling";
import { AdminTiers } from "./pages/admin/AdminTiers";
import { BillingFlagGate } from "./components/admin/billing/BillingFlagGate";
import { SecureNotes } from "./pages/dashboard/SecureNotes";
import { SiteLogins } from "./pages/dashboard/SiteLogins";
import { DashboardBilling } from "./pages/dashboard/DashboardBilling";
import { DashboardUsage } from "./pages/dashboard/DashboardUsage";
import { ToolUsageGate } from "./components/dashboard/ToolUsageGate";
import { CustomerBillingGate } from "./components/dashboard/CustomerBillingGate";
import { UserProfile } from "./pages/dashboard/UserProfile";

export const App = (): JSX.Element => (
  <>
    <ScrollToTop />
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/dashboard" element={<DashboardShell />}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<DashboardOverview />} />
        <Route path="profile" element={<UserProfile />} />
        <Route path="onboarding" element={<OnboardingWizard />} />
        <Route path="connectors" element={<Connectors />} />
        <Route path="monitoring" element={<Monitoring />} />
        <Route path="usage" element={<ToolUsageGate><DashboardUsage /></ToolUsageGate>} />
        <Route path="resources/:slug" element={<ResourceDetail />} />
        <Route path="integrations" element={<IntegrationsCatalog />} />
        <Route path="integrations/:slug" element={<IntegrationDetail />} />
        <Route path="agents/:slug" element={<AgentDetail />} />
        <Route path="secure-notes" element={<SecureNotes />} />
        <Route path="site-logins" element={<SiteLogins />} />
        <Route path="billing" element={<CustomerBillingGate><DashboardBilling /></CustomerBillingGate>} />
        <Route path="admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
          <Route index element={<Navigate to="instances" replace />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="invites" element={<AdminInvites />} />
          <Route path="instances" element={<AdminInstances />} />
          <Route path="instances/:tenantId" element={<AdminInstanceDetail />} />
          {/* Legacy route — the Status tab folded into the per-instance dashboards. */}
          <Route path="status" element={<Navigate to="../instances" replace />} />
          <Route path="events" element={<AdminEvents />} />
          <Route path="tool-calls" element={<AdminToolCalls />} />
          <Route path="tool-usage" element={<AdminToolUsage />} />
          <Route path="rate-limits" element={<AdminRateLimits />} />
          <Route path="plugins" element={<AdminPlugins />} />
          <Route path="announcements" element={<AdminAnnouncements />} />
          <Route path="announcements/:announcementId" element={<AdminAnnouncementDetail />} />
          <Route path="support" element={<AdminSupportMessages />} />
          <Route path="billing" element={<BillingFlagGate><AdminBilling /></BillingFlagGate>} />
          <Route path="tiers" element={<BillingFlagGate><AdminTiers /></BillingFlagGate>} />
          <Route path="resources" element={<AdminResources />} />
          <Route path="integrations" element={<AdminIntegrations />} />
          {/* Legacy route — Interest tab was renamed to Events. */}
          <Route path="interest" element={<Navigate to="../events" replace />} />
          <Route path="ai-models" element={<AiModels />} />
          <Route path="feature-flags" element={<AdminFeatureFlags />} />
          <Route path="secure-notes" element={<AdminSecureNotes />} />
          <Route path="audit" element={<AuditTrail />} />
        </Route>
        <Route path="settings" element={<Navigate to="/dashboard/admin" replace />} />
        <Route path="settings/:tab" element={<Navigate to="/dashboard/admin" replace />} />
        <Route path="ai-models" element={<Navigate to="/dashboard/admin/ai-models" replace />} />
        <Route path="audit" element={<Navigate to="/dashboard/admin/audit" replace />} />
        <Route path="_preview/:section" element={<DashboardSectionPreview />} />
        <Route path="*" element={<Navigate to="overview" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </>
);
